import React from 'react';
import { Box, Button, Flex, Spinner, Tag, Text } from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, ArrowCounterClockwise } from 'phosphor-react';
import { toast } from 'sonner';
import { IKanbanCardArquivado, useKanban } from '../../../../hooks/useKanban';
import { formatDateTimeBR } from '../utils';

interface EstatutoArquivadosProps {
  projetoId?: number;
}

export const EstatutoArquivados: React.FC<EstatutoArquivadosProps> = ({ projetoId }) => {
  const queryClient = useQueryClient();
  const { getCardsArquivados, desarquivarCard } = useKanban();

  const { data, isLoading } = useQuery({
    queryKey: ['estatuto-arquivados', projetoId],
    queryFn: () => getCardsArquivados(projetoId as number),
    enabled: !!projetoId,
  });

  const cards: IKanbanCardArquivado[] = data?.data ?? [];

  const desarquivarMutation = useMutation({
    mutationFn: (cardId: number) => desarquivarCard(cardId),
    onSuccess: () => {
      toast.success('Estatuto desarquivado');
      queryClient.invalidateQueries({ queryKey: ['estatuto-arquivados'] });
      queryClient.invalidateQueries({ queryKey: ['kanban-board'] });
      queryClient.invalidateQueries({ queryKey: ['estatutos'] });
      queryClient.invalidateQueries({ queryKey: ['estatuto-dashboard'] });
    },
    onError: () => toast.error('Erro ao desarquivar estatuto'),
  });

  if (isLoading) {
    return (
      <Flex justify="center" py={16}>
        <Spinner size="lg" color="purple.500" thickness="3px" />
      </Flex>
    );
  }

  if (cards.length === 0) {
    return (
      <Flex
        direction="column"
        align="center"
        justify="center"
        py={16}
        gap={2}
        bg="white"
        borderRadius="xl"
        borderWidth="1px"
        borderColor="gray.100"
      >
        <Archive size={28} color="#94A3B8" weight="duotone" />
        <Text fontSize="sm" color="gray.500" fontWeight="600">
          Nenhum estatuto arquivado
        </Text>
        <Text fontSize="xs" color="gray.400">
          Itens arquivados pelo drawer aparecem aqui e podem ser restaurados.
        </Text>
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap={2}>
      {cards.map(card => (
        <Flex
          key={card.id}
          align="center"
          justify="space-between"
          gap={3}
          bg="white"
          borderWidth="1px"
          borderColor="gray.100"
          borderRadius="lg"
          px={4}
          py={3}
        >
          <Box minW={0}>
            <Text fontSize="sm" fontWeight="700" color="gray.800" noOfLines={1}>
              {card.titulo}
            </Text>
            <Flex align="center" gap={2} mt={1} flexWrap="wrap">
              <Tag size="sm" colorScheme="gray" borderRadius="full" fontSize="11px">
                {card.coluna_titulo}
              </Tag>
              <Text fontSize="11px" color="gray.400">
                Arquivado em {formatDateTimeBR(card.arquivado_em)}
              </Text>
            </Flex>
          </Box>
          <Button
            size="sm"
            variant="outline"
            colorScheme="purple"
            leftIcon={<ArrowCounterClockwise size={14} weight="bold" />}
            onClick={() => desarquivarMutation.mutate(card.id)}
            isLoading={desarquivarMutation.isPending && desarquivarMutation.variables === card.id}
            flexShrink={0}
          >
            Desarquivar
          </Button>
        </Flex>
      ))}
    </Flex>
  );
};
